// 원격 화면(<video>)을 무대(`.stage`) 크기에 **비율을 유지한 채 꽉 차게** 맞춘다.
//
// 🔴 왜 필요한가 — 종전 CSS 는 `.stage video { max-width/max-height: 100% }` 뿐이었다.
//    `max-*` 는 창이 작을 때 **줄이기만** 하고, 창이 커지면 영상의 **원래 해상도(수신 스트림 크기)에서 멈춘다**
//    (2026-09-26 사용자 보고 "어느 정도 늘어나다가 더 이상 늘어나지 않고 고정된다").
//    품질 프리셋이 스트림을 줄여 보내면(예: 긴 변 1280) 멈추는 크기는 더 작아진다.
//
// ⇒ 무대의 **내용 영역**(패딩 제외 — 옆에 고정한 채팅 패널 몫이 패딩이다)과 영상의 원래 비율로
//    "들어가는 가장 큰 크기" 를 계산해 `style.width/height` 에 직접 넣는다.
//    `object-fit` 으로 영상 요소를 무대 전체로 늘리는 방법은 쓰지 않는다 — 원격 화면 경계 테두리
//    (`.stage video` 의 border·box-shadow)가 무대 전체로 번져 **상대 화면의 경계가 사라진다**.
//
// 다시 계산하는 때: 무대 크기 변화(창 크기·전체화면·채팅 고정/해제·패널 폭 끌기) = ResizeObserver,
//                  영상 해상도 변화(모니터 전환·품질 변경) = <video> 의 `resize`/`loadedmetadata`.
// 마우스 좌표 변환(`videoPoint`)은 요소의 실제 크기로 계산하므로 여기서 크기를 바꿔도 그대로 맞는다.
import { useEffect } from 'react';
import type { RefObject } from 'react';

/** `.stage video` 의 테두리 두께(px, 한쪽) — 계산한 크기에 테두리가 더해져 무대를 넘지 않게 뺀다. */
const VIDEO_BORDER_PX = 1;

/**
 * 상자(boxW×boxH)에 비율(vw:vh)을 유지하며 들어가는 가장 큰 크기. 정수 px 로 내림(넘침 방지).
 * 입력이 0 이하이면 null(아직 영상이 없거나 무대가 보이지 않는다).
 */
export function fitSize(boxW: number, boxH: number, vw: number, vh: number): { w: number; h: number } | null {
  if (boxW <= 0 || boxH <= 0 || vw <= 0 || vh <= 0) {
    return null;
  }
  const scale = Math.min(boxW / vw, boxH / vh);
  return { w: Math.floor(vw * scale), h: Math.floor(vh * scale) };
}

/** 요소의 내용 영역 크기(패딩 제외). `clientWidth` 는 패딩을 포함하므로 직접 뺀다. */
function contentBox(el: HTMLElement): { w: number; h: number } {
  const cs = getComputedStyle(el);
  const px = (v: string) => parseFloat(v) || 0;
  return {
    w: el.clientWidth - px(cs.paddingLeft) - px(cs.paddingRight),
    h: el.clientHeight - px(cs.paddingTop) - px(cs.paddingBottom),
  };
}

/**
 * 무대 크기·영상 해상도가 바뀔 때마다 영상 크기를 다시 맞춘다.
 * @param stageRef 무대(영상의 부모, 위치·크기 기준)
 * @param videoRef 원격 화면 <video>
 */
export function useFitVideo(stageRef: RefObject<HTMLElement | null>, videoRef: RefObject<HTMLVideoElement | null>): void {
  useEffect(() => {
    const stage = stageRef.current;
    const video = videoRef.current;
    if (!stage || !video) {
      return;
    }
    const fit = () => {
      const box = contentBox(stage);
      const border = VIDEO_BORDER_PX * 2;
      const s = fitSize(box.w - border, box.h - border, video.videoWidth, video.videoHeight);
      if (!s) {
        return; // 첫 프레임 전·숨은 탭 — 크기를 건드리지 않는다(CSS max-* 가 그대로 적용)
      }
      video.style.width = `${s.w}px`;
      video.style.height = `${s.h}px`;
    };
    const ro = new ResizeObserver(fit);
    ro.observe(stage);
    video.addEventListener('loadedmetadata', fit);
    video.addEventListener('resize', fit); // 영상 해상도 변화(모니터 전환·품질 변경)
    fit();
    return () => {
      ro.disconnect();
      video.removeEventListener('loadedmetadata', fit);
      video.removeEventListener('resize', fit);
    };
  }, [stageRef, videoRef]);
}
